// formula_validator.js
// Designer-side: parse-only check + variable extraction.
// Returns: { ok: Boolean, vars: String[], error?: String }
//
// GESPIEGELTE REGEL — beide Seiten in einer Beauftragung aendern.
// Dieselbe Formelsprache wird ein zweites Mal ausgewertet, in
// inspect-backend/inspect/services/formula_engine.js (dort rechnet der
// Report; hier wird nur geparst). Der Designer ist die Referenz, die
// Report-Engine zieht nach. Verzeichnet ist die Regel in
// contracts/checklist-formula-language.md (Orchestrator-Repo 8p2-chief):
// Normalisierung des Dezimalkommas, Operator-Konfiguration,
// Funktions-/Unaer-Bibliothek, Literal-Zweig, Variablenbegriff.
// Wer hier etwas aendert (Normalisierung, operators-Liste, expr-eval-Version),
// aendert die Formelsprache — dann muessen formula_engine.js und der Vertrag
// im selben Auftrag mit. Eine Aenderung nur auf einer Seite meldet dem
// Anwender "ok" und liefert im Bericht einen stillen Fehlwert.
// Festgenagelt in tests/unit/FormulaValidatorLanguage.spec.ts.

import { Parser } from "expr-eval";

const parser = new Parser({
  operators: {
    add: true, subtract: true, multiply: true, divide: true, remainder: true,
    power: false, factorial: false, comparison: false, logical: false,
    in: false, assignment: false, conditional: false,
  },
});

export function validateFormulaSyntax(formula) {
  if (!formula || typeof formula !== "string" || formula.trim() === "") {
    return { ok: true, vars: [] };
  }
  if (/^[\d.,\s\-+]+$/.test(formula.trim())) {
    return { ok: true, vars: [] };
  }
  try {
    // Convert German decimal commas to points first
    const normalized = formula.replace(/(\d),(\d)/g, "$1.$2");
    const parsed = parser.parse(normalized);
    return { ok: true, vars: parsed.variables() };
  } catch (e) {
    return { ok: false, vars: [], error: e.message };
  }
}

// Namensform der checklistenweiten Variablen aus einer Zeilenschluessel-Spalte:
// zwei durch `_` getrennte gueltige Bezeichner, also `<Schluessel>_<calcFormulaId>`
// (z. B. `Un_nenn`). Genau diese Form erzeugt der Vorlauf der Report-Engine im
// inspect-backend; die Schluesselregel selbst lautet `^[A-Za-z_][A-Za-z0-9_]*$`.
//
// GESPIEGELTE REGEL, zweite Seite: verzeichnet in
// contracts/checklist-formula-language.md (Abschnitt "Variablen").
const ROW_KEY_GLOBAL_NAME = /^[A-Za-z_][A-Za-z0-9_]*_[A-Za-z_][A-Za-z0-9_]*$/;

export function isRowKeyGlobalName(name) {
  return typeof name === "string" && ROW_KEY_GLOBAL_NAME.test(name);
}

// Validate that all variables referenced in formula are declared as calcFormulaId
// somewhere on the same task's formFields.
//
// `currentFormFields` ist aufgabenlokal (der Designer bekommt die Fragenliste
// mit dem Filter {cl_uid, task_uid, removed:false}). Eine Variable aus einer
// Zeilenschluessel-Spalte steht dagegen in einer ANDEREN Aufgabe und kann hier
// nicht bekannt sein. Namen in der Form `<Schluessel>_<ID>` werden deshalb
// nicht gemeldet.
//
// Preis dieser Toleranz, ausdruecklich entschieden: ein Tippfehler in einem
// Namen MIT Unterstrich (`I_nen` statt `I_nenn`) faellt hier nicht mehr auf.
// Die Meldung ist reine Anzeige und sperrt das Speichern nicht.
export function findUnknownVars(formula, currentFormFields, currentFormFieldId) {
  const { ok, vars } = validateFormulaSyntax(formula);
  if (!ok) return [];
  const knownIds = (currentFormFields || [])
    .filter((ff) => ff && ff._id !== currentFormFieldId)
    .map((ff) => ff.calcFormulaId)
    .filter((id) => id && id.length > 0);
  return vars.filter((v) => !knownIds.includes(v) && !isRowKeyGlobalName(v));
}
